import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  QuickBasicsData,
  VerificationContactData,
  INDUSTRIES,
} from "@/domain/intake";

interface ContactSubmitStepProps {
  contactData: VerificationContactData;
  quickBasics: QuickBasicsData;
  onContactChange: (data: VerificationContactData) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function ContactSubmitStep({
  quickBasics,
  onSubmit,
  onBack,
  isSubmitting,
}: ContactSubmitStepProps) {
  const isValid = quickBasics.timezone !== "";

  const industryName =
    INDUSTRIES.find((i) => i.id === quickBasics.industryId)?.name ?? "";

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold mb-2">
          Contact & Review
        </h2>
        <p className="text-muted-foreground mb-6">
          We'll verify your email before scheduling — this protects
          calendar time and prevents spam.
        </p>

        <div className="space-y-6">
          <div className="rounded-lg border border-border/50 divide-y divide-border/50">
            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Your Details
              </p>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{quickBasics.name}</p>
                </div>
                {quickBasics.companyName && (
                  <div>
                    <p className="text-muted-foreground">Company</p>
                    <p className="font-medium">{quickBasics.companyName}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{quickBasics.email}</p>
                </div>
                {industryName && (
                  <div>
                    <p className="text-muted-foreground">Industry</p>
                    <p className="font-medium">{industryName}</p>
                  </div>
                )}
                {quickBasics.timezone && (
                  <div>
                    <p className="text-muted-foreground">Time Zone</p>
                    <p className="font-medium">{quickBasics.timezone}</p>
                  </div>
                )}
                <div className="md:col-span-2">
                  <p className="text-muted-foreground">Discussion Topic</p>
                  <p className="font-medium">{quickBasics.discussionTopic}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-sm font-medium mb-2">What happens next</p>
            <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li>We'll send a verification code to {quickBasics.email}</li>
              <li>You'll schedule your discovery call via Calendly</li>
            </ol>
          </div>

          <div className="pt-4 flex justify-between">
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!isValid || isSubmitting}
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit & Verify"
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
